import * as Sentry from "@sentry/react";

import { allowlistBusinessEvents } from "./event-allowlist";
import { redactUrlParams } from "./redact-url-params";
import { fetchAnalyticsConfig } from "./remote-kill-switch";

const API_HOST = "https://us.i.posthog.com";
const UI_HOST = "https://us.posthog.com";

const scheduleIdle = (task: () => void) => {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(task, { timeout: 5000 });
    return;
  }
  window.setTimeout(task, 0);
};

let initPromise: Promise<void> | null = null;

/**
 * Dynamically imports posthog-js so it lands in its own chunk and never enters
 * the entry bundle.
 *
 * Everything expensive is disabled here rather than in PostHog's project
 * settings. The SDK gates its heavy features on `serverEnabled && clientEnabled`,
 * so a `false` below is a lock no dashboard toggle can open — which is what was
 * missing when PostHog last ran on this app.
 */
const initPostHog = (): Promise<void> => {
  if (initPromise) return initPromise;

  const token = process.env["REACT_APP_POSTHOG_KEY"];
  if (!token) return Promise.resolve();

  initPromise = import("posthog-js/dist/module.slim").then(
    ({ default: posthog }) => {
      // posthog.init() is a no-op once __loaded is set, so whoever runs first
      // wins and their config silently sticks. A GTM tag reaching this page
      // before us would override every lock below without any visible error.
      if (window.posthog?.__loaded) {
        Sentry.captureMessage(
          "PostHog was already initialized before useAnalyticsInit ran",
          "warning"
        );
        return;
      }

      posthog.init(token, {
        api_host: API_HOST,
        ui_host: UI_HOST,
        defaults: "2025-05-24",

        person_profiles: "identified_only",

        // "history_change" is a lie in this build: module.slim ships without the
        // history.pushState patch, so the SDK only ever emits the pageview for
        // the hard load and every react-router navigation goes unrecorded —
        // which is the backbone of both conversion questions. Verified against
        // 1.428.1: `pushState` appears in module.js and module.full.js, not in
        // module.slim.js. So the SDK keeps the first pageview and
        // useAnalyticsInit fires the rest from the router.
        capture_pageview: true,
        capture_pageleave: "if_capture_pageview",

        autocapture: false,
        capture_performance: false,
        disable_session_recording: true,
        disable_surveys: true,

        advanced_disable_feature_flags_on_first_load: true,

        before_send: [allowlistBusinessEvents, redactUrlParams],
      });

      window.posthog = posthog;
    }
  );

  return initPromise;
};

let readyPromise: Promise<boolean> | null = null;

/**
 * The one gate every analytics call awaits — created once, never polled.
 * `posthog.identify()`/`.capture()` called before `init()` resolves are
 * silently dropped by the SDK (it checks `__loaded` and warns, it does not
 * queue), so every helper below awaits this instead of calling posthog-js
 * directly. Resolves `true` once `window.posthog.__loaded` is set, `false`
 * when analytics is off (env flag, remote kill switch) or failed to load.
 */
export const ensureAnalyticsReady = (): Promise<boolean> => {
  if (readyPromise) return readyPromise;

  if (process.env["REACT_APP_POSTHOG_ENABLED"] !== "true") {
    readyPromise = Promise.resolve(false);
    return readyPromise;
  }

  readyPromise = fetchAnalyticsConfig().then((config) => {
    if (!config.postHogEnabled) return false;
    return new Promise<boolean>((resolve) => {
      scheduleIdle(() => {
        initPostHog()
          .then(() => resolve(!!window.posthog?.__loaded))
          .catch(() => resolve(false));
      });
    });
  });

  return readyPromise;
};

export const identify = (
  distinctId: string,
  traits?: Record<string, unknown>
): void => {
  ensureAnalyticsReady().then((ready) => {
    if (ready) window.posthog?.identify(distinctId, traits);
  });
};

export const setTraits = (
  traits: Record<string, unknown>,
  setOnce?: Record<string, unknown>
): void => {
  ensureAnalyticsReady().then((ready) => {
    if (ready) window.posthog?.setPersonProperties(traits, setOnce);
  });
};

/** The route-change half of $pageview — see capture_pageview above. */
export const capturePageview = (): void => {
  ensureAnalyticsReady().then((ready) => {
    if (ready) window.posthog?.capture("$pageview");
  });
};

export const resetIdentity = (): void => {
  ensureAnalyticsReady().then((ready) => {
    if (ready) window.posthog?.reset();
  });
};
